<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import BatchSpeedUpDiscountBadge from '@/components/BatchSpeedUpDiscountBadge.vue'
import { ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import { useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import { computed, ref } from 'vue'
import { useBatchFastForwardsCooldown } from '@/composables/game/upgrade'
import { T } from '@tolgee/vue'
import { formatDuration } from '@/helpers/date'
import { formatUnshortenedNumber } from '@/helpers/utils'
import ThePremium from '@/components/ThePremium.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { hideTonPayment } from '@/composables/init'
import { isBaseMiniApp } from '@/services/base'
import { useConfig } from '@/composables/game/config'
import { useConfetti } from '@/composables/game/confetti'
import { useToaster } from '@/composables/game/toasters'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'
import { usePlayer } from '@/composables/game/player'
import CoinIconWebp from '@/components/icons/CoinIconWebp.vue'
import { env } from '@/env'

const props = defineProps<{
	show: boolean
	discount: number
	ecommerceItem: EcommerceItem
}>()

defineEmits<{
	(e: 'hide'): void
}>()

const {
  isBuyingWithStars,
  isBuyingWithTon,
  isBuyingWithDropee,
  isBuyingWithUsdt,
  isBuyingWithGoldenCoins,
  onPurchase,
  selectedPriceType,
} = useEcommerceItemDetailsPurchase(props.ecommerceItem)
const { syncConfig } = useConfig()
const { showConfetti } = useConfetti()
const { notify } = useToaster()
const {
  discountedTotalPrices,
  totalUnfilteredRemainedTime,
  unfilteredUpgradesWithCooldown,
  batchFastForwardPurchased,
} = useBatchFastForwardsCooldown()
const { player, isBasePlatform, goldenCoinValue } = usePlayer()
const showTonPurchase = computed(() => !isBaseMiniApp && !hideTonPayment.value && discountedTotalPrices.value.tonAmount > 0)
const hasSufficientStars = computed(() => discountedTotalPrices.value.starAmount <= (player.value?.ecommerce?.starsBalance ?? 0))
const showStarPaymentOption = computed(() => !isBasePlatform.value || (discountedTotalPrices.value.starAmount > 0 && hasSufficientStars.value))
const showUsdtPurchase = computed(() => isBaseMiniApp && discountedTotalPrices.value.usdtAmount > 0)
const goldenCoinCoinCost = computed(() => {
  if (goldenCoinValue.value <= 0) return null
  return discountedTotalPrices.value.goldenCoinAmount * goldenCoinValue.value
})
const hasSufficientGoldenCoins = computed(() => goldenCoinCoinCost.value !== null && goldenCoinCoinCost.value <= (player.value?.coins ?? 0))
const showGoldenCoinPaymentOption = computed(() => goldenCoinValue.value > 0 && discountedTotalPrices.value.goldenCoinAmount > 0 && hasSufficientGoldenCoins.value)

const formattedRemainedTime = computed(() => formatDuration(totalUnfilteredRemainedTime.value, 'hh:mm:ss'))
const spedUpFormattedTime = ref(formatDuration(totalUnfilteredRemainedTime.value, 'hh:mm'))
const spedUpCardCount = ref(unfilteredUpgradesWithCooldown.value.length)

const isLoading = ref(false)

async function _onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isLoading.value = true
  spedUpFormattedTime.value = formatDuration(totalUnfilteredRemainedTime.value, 'hh:mm')
  spedUpCardCount.value = unfilteredUpgradesWithCooldown.value.length
  try {
    const response = await onPurchase(priceType)
    if (response.paid) {
      showConfetti()
      batchFastForwardPurchased.value = true
      notify({
        type: 'success',
        title: `${spedUpCardCount.value} card${spedUpCardCount.value > 1 ? 's' : ''} successfully sped up!`,
      })
      await syncConfig()
      isLoading.value = false
      selectedPriceType.value = null
    } else {
      isLoading.value = false
      selectedPriceType.value = null
    }
  } catch (e) {
    isLoading.value = false
    selectedPriceType.value = null
  }
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    @hide="$emit('hide')"
  >
    <div class="w-full flex flex-col items-center relative">
      <StarsBalancePill
        v-if="!batchFastForwardPurchased"
        class="absolute left-0 -top-6"
      />
      <div class="flex items-center relative scale-up-hard">
        <img
          class="max-[375px]:w-[150px] w-[200px] object-cover"
          src="@/assets/images/batch-speed-up.webp"
        >
        <BatchSpeedUpDiscountBadge
          v-if="!batchFastForwardPurchased"
          :discount="discount"
          class="absolute top-3 right-1 rotate-[15deg] !w-[96px] !h-[51px] max-[375px]:scale-[0.6] max-[375px]:origin-right"
          label-class="text-[25px] leading-[28px] discount-label"
        />
      </div>
      <template v-if="!batchFastForwardPurchased">
        <div class="max-[375px]:mt-0 mt-6 text-center text-[24px] leading-8 font-bold -tracking-[0.48px]">
          BATCH SPEED UP = <span class="line-through">${{ discountedTotalPrices.originalUsdAmount.toFixed(2) }}</span>
          ${{ discountedTotalPrices.starUsdAmount.toFixed(2) }}
        </div>
        <div class="text-center text-base text-white/60 font-medium">
          Speed-up all cards in one go
        </div>
        <div class="mt-6 flex rounded-lg p-0.5 bg-gold-gradient">
          <div class="rounded-md bg-dark-bottom-layer px-[30px] py-2.5 flex items-center justify-center">
            <div class="text-white text-lg font-semibold">
              <T
                key-name="upgrades.batch_fast_forward.cards_count"
                default-value="{count, plural, one {# card} other {# cards}}"
                :params="{count: unfilteredUpgradesWithCooldown.length}"
              />
            </div>
          </div>
        </div>
        <div class="text-center mt-1.5 flex flex-wrap items-center gap-x-2 text-sm font-medium text-white/60">
          <span>In total, you will speed up </span>
          <span class="disable-slashed-zero text-white font-semibold">{{ formattedRemainedTime }}</span>
        </div>

        <div
          class="max-[375px]:mt-3 mt-8 w-full flex flex-col gap-y-3"
          :class="{'pointer-events-none': isLoading}"
        >
          <div
            v-if="!env.app.isProd && discountedTotalPrices.dropeeAmount"
            class="relative w-full"
          >
            <div class="z-[1] absolute right-3 -top-1 rotate-[20deg]">
              <TonDiscountBadge label="-20%" />
            </div>
            <BaseButton
              style-type="primary-pink"
              :loading="isBuyingWithDropee"
              class="relative w-full !text-black"
              @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.DROPEE)"
            >
              <span
                v-if="!isBuyingWithDropee"
                class="flex items-center gap-x-2"
              >
                <T
                  key-name="starter_pack.buy_with"
                  default-value="Buy with"
                />
                <img
                  src="@/assets/images/dropee-token-new.webp"
                  class="size-5 min-w-[20px] object-cover"
                  alt="Dropee token"
                >
                <span>{{ discountedTotalPrices.dropeeAmount }}</span>
              </span>
              <T
                v-else
                key-name="lingo.giveaway.transaction.loading_cta"
                default-value="Processing..."
              />
            </BaseButton>
          </div>

          <BaseButton
            v-if="showGoldenCoinPaymentOption && goldenCoinCoinCost !== null"
            style-type="primary-pink"
            class="w-full"
            :loading="isBuyingWithGoldenCoins"
            :disabled="goldenCoinCoinCost === null"
            @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.GOLDEN_COIN)"
          >
            <span
              v-if="!isBuyingWithGoldenCoins"
              class="flex items-center gap-x-2 text-black"
            >
              <CoinIconWebp class="size-5 min-w-[20px] object-contain" />
              <span>{{ formatUnshortenedNumber(goldenCoinCoinCost) }}</span>
            </span>
            <T
              v-else
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
          </BaseButton>

          <BaseButton
            v-if="showStarPaymentOption"
            class="w-full"
            :loading="isBuyingWithStars"
            @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
          >
            <span
              v-if="!isBuyingWithStars"
              class="flex items-center gap-x-2"
            >
              <T
                key-name="starter_pack.buy_with"
                default-value="Buy with"
              />
              <ThePremium :with-tooltip="false" />
              <span class="-ml-0.5">{{ formatUnshortenedNumber(discountedTotalPrices.starAmount) }}</span>
            </span>
            <T
              v-else
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
          </BaseButton>

          <BaseButton
            v-if="showUsdtPurchase"
            style-type="secondary"
            :loading="isBuyingWithUsdt"
            class="w-full"
            @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.USDT)"
          >
            <span
              v-if="!isBuyingWithUsdt"
              class="flex items-center gap-x-2"
            >
              <T
                key-name="starter_pack.buy_with"
                default-value="Buy with"
              />
              <img
                src="@/assets/images/usdc.png"
                class="size-5 min-w-[20px] object-cover"
                alt="USDC symbol"
              >
              <span>{{ discountedTotalPrices.usdtAmount }}</span>
            </span>
            <T
              v-else
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
          </BaseButton>

          <template v-if="showTonPurchase">
            <div class="relative w-full">
              <div class="absolute right-3 -top-1 rotate-[20deg]">
                <TonDiscountBadge label="-10%" />
              </div>
              <BaseButton
                style-type="secondary"
                :loading="isBuyingWithTon"
                class="w-full"
                @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
              >
                <span
                  v-if="!isBuyingWithTon"
                  class="flex items-center gap-x-2"
                >
                  <T
                    key-name="starter_pack.buy_with"
                    default-value="Buy with"
                  />
                  <img
                    src="@/assets/images/ton-symbol.png"
                    class="size-5 min-w-[20px] object-cover"
                  >
                  <span>{{ discountedTotalPrices.tonAmount }}</span>
                </span>
                <T
                  v-else
                  key-name="lingo.giveaway.transaction.loading_cta"
                  default-value="Processing..."
                />
              </BaseButton>
            </div>

            <div
              class="mx-auto -mt-1 max-w-[307px] text-center text-sm -tracking-[0.28px] text-white/[.87] font-medium"
            >
              <T
                key-name="transaction.process_description"
                default-value="Transaction may take a few minutes to process. You’ll be notified once it’s successful."
              />
            </div>
          </template>
        </div>
      </template>
      <template v-else>
        <div class="mt-3 text-center text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
          Purchase successful!
        </div>
        <div class="text-center text-base font-medium">
          You saved {{ spedUpFormattedTime }}!
        </div>
        <BaseButton
          class="w-full mt-12"
          @click="$emit('hide')"
        >
          Close
        </BaseButton>
      </template>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
.bg-gold-gradient {
	background: linear-gradient(
			to right,
			#fff246 0%,
			#e5b80b 37%,
			#fff571 69%,
			#e5b80b 100%
	);
}
</style>
<style lang="scss">
.discount-label {
	&.label-white-stroke {
		-webkit-text-stroke-width: 5px !important;
	}
}
</style>
